import { todayISO } from '@/lib/wareki';
import type { NoticeDetail, NoticeInput, NoticeVesselDto, NoticeVesselInput } from './api';

export type NoticeVesselForm = NoticeVesselInput & { unlisted: boolean; saved: boolean; savedDeleted: boolean };
export type NoticeForm = Omit<NoticeInput, 'vessels' | 'confirmations'> & { vessels: NoticeVesselForm[] };

export const emptyVesselRow = (): NoticeVesselForm => ({
  rowKey: null,
  vesselId: null,
  businessOperatorId: null,
  unlistedVesselName: null,
  unlistedReason: null,
  phone: '',
  crewCount: null,
  portName: '',
  departureDate: '',
  departureHour: null,
  departureMinute: 0,
  arrivalDate: '',
  arrivalHour: null,
  arrivalMinute: 0,
  zoneNumbers: '',
  catchSurveyPlanned: false,
  catchSalePlanned: false,
  note: '',
  changeType: null,
  vesselChangeReason: null,
  phoneChangeReason: null,
  crewChangeReason: null,
  portChangeReason: null,
  departureChangeReason: null,
  arrivalChangeReason: null,
  zoneChangeReason: null,
  unlisted: false,
  saved: false,
  savedDeleted: false,
});

export function emptyNoticeForm(year: number, businessOperatorId: number | null): NoticeForm {
  return {
    fiscalYear: year,
    businessOperatorId,
    reporterName: '',
    reportDate: todayISO(),
    changeReason: '',
    vessels: [emptyVesselRow(), emptyVesselRow()],
  };
}

function rowFromDto(v: NoticeVesselDto): NoticeVesselForm {
  return {
    ...emptyVesselRow(),
    ...v,
    phone: v.phone ?? '',
    note: v.note ?? '',
    // 編集開始時は変更区分を「変更無し」に戻し、削除済みの行のみ「削除」を維持する（kintone resetSubTableChangeCategory）
    changeType: v.changeType === '削除' ? '削除' : null,
    unlisted: v.vesselId == null,
    saved: true,
    savedDeleted: v.changeType === '削除',
  };
}

export function noticeFormFromDetail(d: NoticeDetail): NoticeForm {
  return {
    fiscalYear: d.fiscalYear,
    businessOperatorId: d.businessOperatorId,
    reporterName: d.reporterName,
    reportDate: d.reportDate,
    registrationType: d.registrationType === '中止' ? '中止' : '変更',
    changeReason: d.changeReason ?? '',
    vessels: d.vessels.map(rowFromDto),
  };
}

const nz = (v: string | null | undefined) => (v == null || v.trim() === '' ? null : v);

export function toNoticeInput(f: NoticeForm, confirmations: Record<string, boolean>, id?: number): NoticeInput {
  return {
    ...f,
    id,
    changeReason: nz(f.changeReason),
    vessels: f.vessels.map(({ unlisted, saved: _s, savedDeleted: _d, ...r }) => ({
      ...r,
      vesselId: unlisted ? null : r.vesselId,
      businessOperatorId: unlisted ? null : r.businessOperatorId,
      unlistedVesselName: unlisted ? nz(r.unlistedVesselName) : null,
      unlistedReason: unlisted ? nz(r.unlistedReason) : null,
      phone: nz(r.phone),
      note: nz(r.note),
      departureDate: r.departureDate || (null as unknown as string),
      arrivalDate: r.arrivalDate || (null as unknown as string),
    })),
    confirmations,
  };
}
